import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  type Chart,
  type ChartAccount,
  type ChartSource,
  type ChartPreviewAccount,
  type ChartTemplateInfo,
  type Company,
  type FiscalYear,
  type NewAccount,
  type StatementPosition,
} from '../api';
import type { Dictionary } from '../i18n';
import * as Icon from '../components/Icons';
import { ErrorAlert, failureOf, type Failure } from '../components/Alerts';
import { FiscalYearGap } from '../components/FiscalYearGap';
import type { Panel } from '../components/Shell';
import { fiscalYearLabel } from '../fiscalYear';

/**
 * Zakładowy plan kont (E7-S1).
 *
 * Plan należy do roku obrotowego: rok wybiera się na górze, a każdy rok ma własny komplet kont.
 * Ekran jest ułożony **zespołami (0–9)**, a nie jedną listą czterdziestu numerów — tak plan kont
 * czyta się w każdym programie księgowym i tak jest napisany w polityce rachunkowości.
 */

const EMPTY_ACCOUNT: NewAccount = {
  number: '',
  name: '',
  kind: 'BALANCE',
  side: 'DEBIT',
  settlement: false,
  currencyAccount: false,
  taxTreatment: null,
  systemRole: null,
  tags: [],
  active: true,
  statementPositionDebit: null,
  statementPositionCredit: null,
};

/**
 * Rok, który pokazujemy po wejściu na ekran.
 *
 * Ten, w którym stoimy dziś — a nie pierwszy z listy. Księgowa wchodzi w plan kont, żeby
 * dopisać konto do roku, który właśnie prowadzi; otwieranie jej roku sprzed trzech lat znaczyłoby,
 * że zmiany trafiają nie tam, gdzie trzeba, i że widać to dopiero po zapisaniu.
 */
export function currentYear(years: FiscalYear[]): FiscalYear | null {
  const today = new Date().toISOString().slice(0, 10);
  return years.find((year) => year.startsOn <= today && year.endsOn >= today) ?? years[0] ?? null;
}

export function ChartPanel({
  company,
  t,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  onGo: (panel: Panel) => void;
}) {
  const [years, setYears] = useState<FiscalYear[] | null>(null);
  const [yearId, setYearId] = useState<string | null>(null);
  const [chart, setChart] = useState<Chart | null>(null);
  const [templates, setTemplates] = useState<ChartTemplateInfo[]>([]);
  /* Pozycje załącznika, według którego ten plan sporządza sprawozdanie — do wyboru w formularzu konta */
  const [positions, setPositions] = useState<StatementPosition[]>([]);
  const [source, setSource] = useState<ChartSource>('TEMPLATE');
  const [templateId, setTemplateId] = useState<string>('');
  /** Co powstanie po wybraniu tego źródła; null, dopóki nie wiemy. */
  const [preview, setPreview] = useState<ChartPreviewAccount[] | null>(null);
  const [form, setForm] = useState<{ draft: NewAccount; id: string | null } | null>(null);
  const [templateName, setTemplateName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const editable = company.roles.includes('ACCOUNTANT');

  useEffect(() => {
    api
      .fiscalYears(company.id)
      .then((loaded) => {
        setYears(loaded);
        setYearId((current) => current ?? currentYear(loaded)?.id ?? null);
      })
      .catch((failure) => setError(failureOf(failure)));
  }, [company.id]);

  const load = useCallback(async () => {
    if (!yearId) return;
    setError(null);
    try {
      const [loaded, available] = await Promise.all([
        api.chart(company.id, yearId),
        api.chartTemplates(company.id),
      ]);
      setChart(loaded ?? null);
      setTemplates(available);
      setTemplateId((current) => current || available[0]?.id || '');
      setPositions(loaded ? await api.statementPositions(company.id, loaded.statementTemplate) : []);
    } catch (failure) {
      setChart(null);
      setError(failureOf(failure));
    }
  }, [company.id, yearId]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Podgląd tego, co powstanie. Nazwa wzorca nie mówi nic o tym, jakie konta się w nim znajdą,
   * więc wybór „z wzorca" był wyborem na ślepo. Listę liczy serwer tym samym kodem, którym
   * potem zapisuje plan — inaczej podgląd mógłby z czasem przestać mówić prawdę.
   */
  useEffect(() => {
    if (!yearId || chart !== null) return;
    if (source === 'TEMPLATE' && !templateId) return;
    let stale = false;
    api
      .chartPreview(company.id, yearId, source, source === 'TEMPLATE' ? templateId : undefined)
      .then((accounts) => {
        if (!stale) setPreview(accounts);
      })
      .catch(() => {
        // Brak podglądu nie jest usterką do zgłaszania: powód (np. brak poprzedniego roku)
        // i tak padnie przy zakładaniu planu, a dwa komunikaty naraz tylko przeszkadzają
        if (!stale) setPreview(null);
      });
    return () => {
      stale = true;
    };
  }, [company.id, yearId, chart, source, templateId]);

  const year = years?.find((item) => item.id === yearId) ?? null;
  const closed = year?.status === 'CLOSED';

  /** Konta pogrupowane zespołami; puste zespoły w ogóle się nie pokazują. */
  const groups = useMemo(() => {
    const byGroup = new Map<number, ChartAccount[]>();
    for (const account of chart?.accounts ?? []) {
      byGroup.set(account.group, [...(byGroup.get(account.group) ?? []), account]);
    }
    return [...byGroup.entries()].sort(([a], [b]) => a - b);
  }, [chart]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.chart.title}</h3>
          <p>{t.chart.lead}</p>
        </div>
        <div className="sp">
          <select
            className="inp w"
            aria-label={t.chart.year}
            value={yearId ?? ''}
            onChange={(event) => {
              setYearId(event.target.value);
              setForm(null);
            }}
          >
            {years?.map((item) => (
              <option key={item.id} value={item.id}>
                {fiscalYearLabel(item)}
                {item.status === 'CLOSED' ? ` · ${t.periods.closed}` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <ErrorAlert error={error} t={t} onGo={onGo} />

      <FiscalYearGap
        companyId={company.id}
        roles={company.roles}
        years={years}
        t={t}
        onOpened={() => {
          // Nowy rok od razu staje się tym wybranym — po to się go otwiera
          void api.fiscalYears(company.id).then((loaded) => {
            setYears(loaded);
            setYearId(currentYear(loaded)?.id ?? null);
          });
        }}
      />

      {/*
        * Dwa różne powody, więc dwa różne zdania: „nie wiemy jeszcze" znaczy brak ustawień
        * (albo ustawienia obowiązujące dopiero od później), a „nie prowadzi" — inny rodzaj ksiąg.
        * Wspólny komunikat kazałby szukać nie tam, gdzie trzeba.
        */}
      {company.booksType !== 'FULL' && (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>{company.booksType == null ? t.chart.booksUnknown : t.chart.notFullBooks}</b>
            <p>{company.booksType == null ? t.chart.booksUnknownHint : t.chart.notFullBooksHint}</p>
          </div>
          <button type="button" className="btn" onClick={() => onGo('settings')}>
            {t.dashboard.fixSettings}
          </button>
        </div>
      )}

      {!editable && (
        <div className="alert a-info">
          <Icon.Info />
          <div>
            <b>{t.chart.onlyAccountant}</b>
            <p>{t.chart.onlyAccountantHint}</p>
          </div>
        </div>
      )}

      {years?.length === 0 && (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>{t.chart.noYear}</b>
          </div>
          <button type="button" className="btn" onClick={() => onGo('periods')}>
            {t.chart.goToPeriods}
          </button>
        </div>
      )}

      {chart === null && yearId && years !== null && years.length > 0 && (
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.chart.empty}</h4>
              <p>{t.chart.emptyHint}</p>
            </div>
          </div>
          <div className="row">
            <label className="fld">
              <span>{t.chart.source}</span>
              <select
                className="inp"
                value={source}
                onChange={(event) => setSource(event.target.value as ChartSource)}
                disabled={!editable || closed}
              >
                <option value="TEMPLATE">{t.chart.sourceTemplate}</option>
                <option value="PREVIOUS_YEAR">{t.chart.sourcePrevious}</option>
                <option value="EMPTY">{t.chart.sourceEmpty}</option>
              </select>
            </label>
            {source === 'TEMPLATE' && (
              <label className="fld">
                <span>{t.chart.template}</span>
                <select
                  className="inp"
                  value={templateId}
                  onChange={(event) => setTemplateId(event.target.value)}
                  disabled={!editable || closed}
                >
                  {templates.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} · {t.chart.scopes[item.scope]}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button
              type="button"
              className="btn"
              disabled={!editable || closed || busy || (source === 'TEMPLATE' && !templateId)}
              onClick={() =>
                void run(() =>
                  api.createChart(company.id, yearId, {
                    source,
                    templateId: source === 'TEMPLATE' ? templateId : undefined,
                  }),
                )
              }
            >
              {t.chart.create}
            </button>
          </div>

          {preview && preview.length > 0 && (
            <>
              <p className="note">{t.chart.previewHint(preview.length)}</p>
              {/*
                * Cała lista, bez własnego paska przewijania. Okienko na trzysta pikseli pokazywało
                * sześć kont z czterdziestu trzech i kazało przewijać w przewijanej już stronie —
                * a po to jest podgląd, żeby zobaczyć plan, a nie jego początek.
                */}
              <div className="tbl">
                <table>
                  <thead>
                    <tr>
                      <th>{t.chart.number}</th>
                      <th>{t.chart.name}</th>
                      <th>{t.chart.kind}</th>
                      <th>{t.chart.side}</th>
                      <th>{t.chart.features}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((account) => (
                      <tr key={account.number}>
                        <td className="mono">{account.number}</td>
                        <td className="wrap">{account.name}</td>
                        <td>{t.chart.kinds[account.kind]}</td>
                        <td>{t.chart.sides[account.side]}</td>
                        <td>
                          <span className="st">
                            {account.settlement && <span className="chip st-info">{t.chart.settlement}</span>}
                            {account.currencyAccount && <span className="chip st-info">{t.chart.currency}</span>}
                            {account.systemRole && (
                              <span className="chip st-draft">
                                {t.chart.roles[account.systemRole.toUpperCase() as keyof typeof t.chart.roles]}
                              </span>
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {source === 'EMPTY' && <p className="note">{t.chart.previewEmpty}</p>}
        </div>
      )}

      {chart && (
        <>
          <div className="panel">
            <div className="hd">
              <div>
                <h4>
                  {t.chart.statements[chart.statementTemplate]} · {t.chart.variants[chart.costVariant]}
                </h4>
                <p>{t.chart.accountCount(chart.accounts.length)}</p>
              </div>
              <div className="sp">
                {editable && !closed && (
                  <button type="button" className="btn sec sm" onClick={() => setForm({ draft: EMPTY_ACCOUNT, id: null })}>
                    <Icon.Plus />
                    {t.chart.add}
                  </button>
                )}
                {editable && (
                  <button type="button" className="btn ghost sm" onClick={() => setTemplateName('')}>
                    {t.chart.saveAsTemplate}
                  </button>
                )}
              </div>
            </div>

            {templateName !== null && (
              <div className="row">
                <label className="fld">
                  <span>{t.chart.templateName}</span>
                  <input
                    className="inp"
                    value={templateName}
                    onChange={(event) => setTemplateName(event.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="btn sm"
                  disabled={busy || templateName.trim() === ''}
                  onClick={() =>
                    void run(async () => {
                      await api.saveChartAsTemplate(company.id, chart.fiscalYearId, {
                        name: templateName,
                        scope: 'COMPANY',
                      });
                      setTemplateName(null);
                    })
                  }
                >
                  {t.chart.save}
                </button>
                <button type="button" className="btn plain sm" onClick={() => setTemplateName(null)}>
                  {t.chart.cancel}
                </button>
              </div>
            )}
          </div>

          {form && (
            <AccountForm
              draft={form.draft}
              editing={form.id !== null}
              busy={busy}
              positions={positions}
              t={t}
              onChange={(draft) => setForm({ ...form, draft })}
              onCancel={() => setForm(null)}
              onSave={() =>
                void run(async () => {
                  if (form.id) await api.updateAccount(company.id, chart.fiscalYearId, form.id, form.draft);
                  else await api.addAccount(company.id, chart.fiscalYearId, form.draft);
                  setForm(null);
                })
              }
            />
          )}

          {groups.map(([group, accounts]) => (
            <div className="panel" key={group}>
              <div className="hd">
                <div>
                  <h4>
                    {group} — {t.chart.groups[group as keyof typeof t.chart.groups]}
                  </h4>
                </div>
              </div>
              <div className="tbl">
                <table>
                  <thead>
                    <tr>
                      <th>{t.chart.number}</th>
                      <th>{t.chart.name}</th>
                      <th>{t.chart.kind}</th>
                      <th>{t.chart.side}</th>
                      <th>{t.chart.features}</th>
                      <th>{t.chart.role}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account) => (
                      <tr key={account.id}>
                        <td className="mono">{account.number}</td>
                        <td className="wrap">
                          {account.name}
                          {!account.active && <span className="sm muted"> · {t.chart.inactive}</span>}
                        </td>
                        <td>{t.chart.kinds[account.kind]}</td>
                        <td>{t.chart.sides[account.side]}</td>
                        <td>
                          <span className="st">
                            {/* Konto z analityką jest sumą swoich dzieci i nie przyjmuje zapisów */}
                            {account.hasChildren && <span className="chip st-draft">{t.chart.sum}</span>}
                            {account.settlement && <span className="chip st-info">{t.chart.settlement}</span>}
                            {account.currencyAccount && <span className="chip st-info">{t.chart.currency}</span>}
                            {account.taxTreatment && (
                              <span className="chip st-info">{t.chart.taxes[account.taxTreatment]}</span>
                            )}
                          </span>
                        </td>
                        <td className="sm">
                          {account.systemRole
                            ? t.chart.roles[account.systemRole.toUpperCase() as keyof typeof t.chart.roles]
                            : ''}
                        </td>
                        <td className="act">
                          {editable && !closed && (
                            <div className="row">
                              <button
                                type="button"
                                className="btn ghost sm"
                                onClick={() =>
                                  setForm({
                                    id: account.id,
                                    draft: {
                                      number: account.number,
                                      name: account.name,
                                      kind: account.kind,
                                      side: account.side,
                                      settlement: account.settlement,
                                      currencyAccount: account.currencyAccount,
                                      taxTreatment: account.taxTreatment,
                                      systemRole: (account.systemRole?.toUpperCase() ??
                                        null) as NewAccount['systemRole'],
                                      tags: account.tags,
                                      active: account.active,
                                      statementPositionDebit: account.statementPositionDebit,
                                      statementPositionCredit: account.statementPositionCredit,
                                    },
                                  })
                                }
                              >
                                {t.chart.edit}
                              </button>
                              <button
                                type="button"
                                className="btn ghost sm"
                                disabled={busy}
                                onClick={() =>
                                  void run(() => api.deleteAccount(company.id, chart.fiscalYearId, account.id))
                                }
                              >
                                {t.chart.delete}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );
}

/**
 * Formularz konta.
 *
 * Strona salda znika przy koncie pozabilansowym, a traktowanie podatkowe przy bilansowym —
 * bo serwer i tak odrzuca takie połączenia, a pole, którego nie wolno wypełnić, tylko myli.
 */
function AccountForm({
  draft,
  editing,
  busy,
  positions,
  t,
  onChange,
  onCancel,
  onSave,
}: {
  draft: NewAccount;
  editing: boolean;
  busy: boolean;
  positions: StatementPosition[];
  t: Dictionary;
  onChange: (draft: NewAccount) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const offBalance = draft.kind === 'OFFBALANCE';
  /* Do wyboru tylko pozycje, które da się przypisać: bez formuł, sum bilansu i nagłówków wariantów */
  const assignable = positions.filter((position) => position.assignable);
  const sections = ['ASSETS', 'LIABILITIES', 'INCOME'] as const;
  const positionSelect = (field: 'statementPositionDebit' | 'statementPositionCredit', label: string) => (
    <label className="fld" style={{ flex: 1, minWidth: 260 }}>
      <span>{label}</span>
      <select
        className="inp"
        value={draft[field] ?? ''}
        onChange={(event) => onChange({ ...draft, [field]: event.target.value || null })}
      >
        <option value="">{t.chart.positionNone}</option>
        {sections.map((section) => (
          <optgroup key={section} label={t.chart.sections[section]}>
            {assignable
              .filter((position) => position.section === section)
              .map((position) => (
                <option key={position.code} value={position.code}>
                  {'\u00a0'.repeat(Math.max(0, position.level - 1) * 3)}
                  {position.label}
                </option>
              ))}
          </optgroup>
        ))}
      </select>
    </label>
  );

  return (
    <div className="panel">
      <div className="hd">
        <div>
          <h4>{editing ? t.chart.edit : t.chart.add}</h4>
          <p>{t.chart.numberHint}</p>
        </div>
      </div>
      <div className="row">
        <label className="fld">
          <span>{t.chart.number}</span>
          <input
            className="inp mono"
            style={{ maxWidth: 140 }}
            value={draft.number}
            onChange={(event) => onChange({ ...draft, number: event.target.value })}
          />
        </label>
        <label className="fld" style={{ flex: 1 }}>
          <span>{t.chart.name}</span>
          <input className="inp" value={draft.name} onChange={(event) => onChange({ ...draft, name: event.target.value })} />
        </label>
        <label className="fld">
          <span>{t.chart.kind}</span>
          <select
            className="inp"
            value={draft.kind}
            onChange={(event) => {
              const kind = event.target.value as NewAccount['kind'];
              onChange({
                ...draft,
                kind,
                side: kind === 'OFFBALANCE' ? 'NONE' : draft.side === 'NONE' ? 'DEBIT' : draft.side,
                settlement: kind === 'BALANCE' ? draft.settlement : false,
                taxTreatment: kind === 'RESULT' ? draft.taxTreatment : null,
              });
            }}
          >
            {(['BALANCE', 'RESULT', 'OFFBALANCE'] as const).map((kind) => (
              <option key={kind} value={kind}>
                {t.chart.kinds[kind]}
              </option>
            ))}
          </select>
        </label>
        {!offBalance && (
          <label className="fld">
            <span>{t.chart.side}</span>
            <select
              className="inp"
              value={draft.side}
              onChange={(event) => onChange({ ...draft, side: event.target.value as NewAccount['side'] })}
            >
              {(['DEBIT', 'CREDIT', 'BOTH'] as const).map((side) => (
                <option key={side} value={side}>
                  {t.chart.sides[side]}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="row">
        {draft.kind === 'BALANCE' && (
          <label className="check">
            <input
              type="checkbox"
              checked={draft.settlement}
              onChange={(event) => onChange({ ...draft, settlement: event.target.checked })}
            />
            <span>{t.chart.settlement}</span>
          </label>
        )}
        <label className="check">
          <input
            type="checkbox"
            checked={draft.currencyAccount}
            onChange={(event) => onChange({ ...draft, currencyAccount: event.target.checked })}
          />
          <span>{t.chart.currency}</span>
        </label>
        {draft.kind === 'RESULT' && (
          <label className="fld">
            <span>{t.chart.tax}</span>
            <select
              className="inp"
              value={draft.taxTreatment ?? ''}
              onChange={(event) =>
                onChange({ ...draft, taxTreatment: (event.target.value || null) as NewAccount['taxTreatment'] })
              }
            >
              <option value="">—</option>
              {(['DEDUCTIBLE', 'NON_DEDUCTIBLE', 'TAXABLE', 'NON_TAXABLE'] as const).map((value) => (
                <option key={value} value={value}>
                  {t.chart.taxes[value]}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="fld">
          <span>{t.chart.role}</span>
          <select
            className="inp"
            value={draft.systemRole ?? ''}
            onChange={(event) =>
              onChange({ ...draft, systemRole: (event.target.value || null) as NewAccount['systemRole'] })
            }
          >
            <option value="">—</option>
            {(Object.keys(t.chart.roles) as (keyof typeof t.chart.roles)[]).map((role) => (
              <option key={role} value={role}>
                {t.chart.roles[role]}
              </option>
            ))}
          </select>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={draft.active}
            onChange={(event) => onChange({ ...draft, active: event.target.checked })}
          />
          <span>{t.chart.activeAccount}</span>
        </label>
      </div>

      {/* Konto pozabilansowe nie wchodzi do sprawozdania — nie ma dla niego pozycji */}
      {!offBalance && (
        <>
          <div className="row">
            {positionSelect('statementPositionDebit', t.chart.positionDebit)}
            {positionSelect('statementPositionCredit', t.chart.positionCredit)}
          </div>
          <p className="hint">{t.chart.positionHint}</p>
        </>
      )}

      <div className="row">
        <button type="button" className="btn" disabled={busy} onClick={onSave}>
          {t.chart.save}
        </button>
        <button type="button" className="btn plain" onClick={onCancel}>
          {t.chart.cancel}
        </button>
      </div>
    </div>
  );
}
